import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { AppDispatch, AppThunk } from "@/app/types";
import { fetchWorkItems } from "@/api/capex/fetchWorkItems/fetchWorkItemsApi";
import { fetchSprintIterations } from "@/api/capex/fetchSprintIterations/fetchSprintIterationsApi";
import { PaginationInfo } from "@/api/capex/fetchWorkItems/types";
import {
  ProductBacklogItem,
  SprintIteration,
  CapExFilters,
  StatusFilter,
} from "@/types/capex";
import { getMyUserProfile } from "@/api/getMyUserProfile/getMyUserProfileApi";
import { updateMyUserProfile } from "@/api/updateMyUserProfile/updateMyUserProfileApi";
import {
  fetchUserProfileAction,
  setUserProfile,
} from "@/modules/hub/features/UserAppSettings/slices/userProfileSlice";

interface CapExReportState {
  workItems: ProductBacklogItem[];
  sprintIterations: SprintIteration[];
  filters: CapExFilters;
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  patExists: boolean | null;
  patLoading: boolean;
  patError: string | null;
  patInvalid: boolean;
  // Incremented whenever the token is (re)saved, so pages can refetch
  patVersion: number;
  sprintsLoading: boolean;
  sprintsError: string | null;
}

// Get current month in YYYY-M format
const getCurrentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}`;
};

// Error code returned by the API when Azure DevOps rejects the stored PAT
const AZURE_PAT_INVALID = "AZURE_PAT_INVALID";

// Extract a user-facing message (and any API error code) from a thrown error
const parseApiError = (
  err: unknown,
  fallback: string,
): { message: string; code?: string } => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { error?: string; code?: string }
      | undefined;
    return {
      message: data?.error || err.message || fallback,
      code: data?.code,
    };
  }
  return { message: err instanceof Error ? err.message : fallback };
};

const initialState: CapExReportState = {
  workItems: [],
  sprintIterations: [],
  filters: {
    month: getCurrentMonth(),
    sprintId: undefined,
    status: "all",
  },
  pagination: null,
  loading: false,
  error: null,
  patExists: null,
  patLoading: false,
  patError: null,
  patInvalid: false,
  patVersion: 0,
  sprintsLoading: false,
  sprintsError: null,
};

export const capexReportSlice = createSlice({
  name: "capexReport",
  initialState,
  reducers: {
    // Work items
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setWorkItems(state, action: PayloadAction<ProductBacklogItem[]>) {
      state.workItems = action.payload;
    },
    setPagination(state, action: PayloadAction<PaginationInfo | null>) {
      state.pagination = action.payload;
    },

    // Filters
    setMonth(state, action: PayloadAction<string>) {
      state.filters.month = action.payload;
    },
    setSprintId(state, action: PayloadAction<string | undefined>) {
      state.filters.sprintId = action.payload;
    },
    setStatus(state, action: PayloadAction<StatusFilter>) {
      state.filters.status = action.payload;
    },
    setFilters(state, action: PayloadAction<Partial<CapExFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Sprint iterations
    setSprintsLoading(state, action: PayloadAction<boolean>) {
      state.sprintsLoading = action.payload;
    },
    setSprintsError(state, action: PayloadAction<string | null>) {
      state.sprintsError = action.payload;
    },
    setSprintIterations(state, action: PayloadAction<SprintIteration[]>) {
      state.sprintIterations = action.payload;
    },

    // PAT
    setPatExists(state, action: PayloadAction<boolean | null>) {
      state.patExists = action.payload;
    },
    setPatLoading(state, action: PayloadAction<boolean>) {
      state.patLoading = action.payload;
    },
    setPatError(state, action: PayloadAction<string | null>) {
      state.patError = action.payload;
    },
    setPatInvalid(state, action: PayloadAction<boolean>) {
      state.patInvalid = action.payload;
    },
    bumpPatVersion(state) {
      state.patVersion += 1;
    },

    // Reset
    resetState() {
      return initialState;
    },
  },
});

export default capexReportSlice.reducer;

export const {
  setLoading,
  setError,
  setWorkItems,
  setPagination,
  setMonth,
  setSprintId,
  setStatus,
  setFilters,
  setSprintsLoading,
  setSprintsError,
  setSprintIterations,
  setPatExists,
  setPatLoading,
  setPatError,
  setPatInvalid,
  bumpPatVersion,
  resetState,
} = capexReportSlice.actions;

// Thunks
export const fetchWorkItemsAction =
  (month?: string, sprintId?: string, fetchAll = false): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    dispatch(setPatInvalid(false));

    try {
      if (fetchAll) {
        // Load every page so the caller sees ALL PBIs, not just the first
        // page (default limit 50). Used by the Bulk Hours flow, which must
        // operate on the whole sprint. limit=100 is the API max, so most
        // sprints resolve in a single request; we still loop for larger
        // sprints and de-dupe by id to be safe across paths.
        const pageSize = 100;
        const first = await fetchWorkItems({
          month,
          sprintId,
          page: 1,
          limit: pageSize,
        });
        const byId = new Map<string, ProductBacklogItem>();
        first.items.forEach((item) => byId.set(item.id, item));
        const totalPages = first.pagination.totalPages;
        for (let page = 2; page <= totalPages; page++) {
          const next = await fetchWorkItems({
            month,
            sprintId,
            page,
            limit: pageSize,
          });
          next.items.forEach((item) => byId.set(item.id, item));
        }
        const items = Array.from(byId.values());
        dispatch(setWorkItems(items));
        dispatch(
          setPagination({
            page: 1,
            limit: items.length,
            total: items.length,
            totalPages: 1,
          }),
        );
      } else {
        const response = await fetchWorkItems({ month, sprintId });
        dispatch(setWorkItems(response.items));
        dispatch(setPagination(response.pagination));
      }
    } catch (err) {
      const { message, code } = parseApiError(
        err,
        "Failed to fetch work items",
      );
      if (code === AZURE_PAT_INVALID) {
        dispatch(setPatInvalid(true));
      }
      dispatch(setError(message));
    } finally {
      dispatch(setLoading(false));
    }
  };

export const fetchSprintIterationsAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setSprintsLoading(true));
    dispatch(setSprintsError(null));

    try {
      const iterations = await fetchSprintIterations();
      dispatch(setSprintIterations(iterations));
    } catch (err) {
      const { message, code } = parseApiError(
        err,
        "Failed to fetch sprint iterations",
      );
      if (code === AZURE_PAT_INVALID) {
        dispatch(setPatInvalid(true));
      }
      dispatch(setSprintsError(message));
    } finally {
      dispatch(setSprintsLoading(false));
    }
  };

export const checkPatAction =
  (): AppThunk => async (dispatch: AppDispatch, getState) => {
    dispatch(setPatLoading(true));
    dispatch(setPatError(null));

    try {
      // Check user profile for PAT existence
      const existingProfile = getState().userProfile.userProfile;
      if (existingProfile) {
        dispatch(setPatExists(existingProfile.hasAzureDevOpsPat));
      } else {
        // Fetch profile if not already loaded
        const response = await getMyUserProfile();
        dispatch(setUserProfile(response.data));
        dispatch(setPatExists(response.data.hasAzureDevOpsPat));
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to check PAT status";
      dispatch(setPatError(message));
      dispatch(setPatExists(false));
    } finally {
      dispatch(setPatLoading(false));
    }
  };

export const storePatAction =
  (pat: string): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    dispatch(setPatLoading(true));
    dispatch(setPatError(null));

    try {
      const profile = getState().userProfile.userProfile;
      if (!profile) {
        throw new Error("User profile not loaded");
      }

      // Save PAT via the user profile endpoint (only send non-null fields)
      await updateMyUserProfile({
        firstName: profile.firstName || undefined,
        lastName: profile.lastName || undefined,
        workEmail: profile.workEmail || undefined,
        jobTitle: profile.jobTitle || undefined,
        azureDevOpsPat: pat,
      });

      dispatch(setPatExists(true));
      dispatch(setPatInvalid(false));
      // Signal pages to refetch with the new token
      dispatch(bumpPatVersion());
      // Refresh user profile to reflect the change
      dispatch(fetchUserProfileAction(false));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to store PAT";
      dispatch(setPatError(message));
      throw err;
    } finally {
      dispatch(setPatLoading(false));
    }
  };
